import {
  Component, ElementRef, ViewChild, AfterViewInit,
  OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef,
} from '@angular/core';
import { BaseCellEditor } from './base-editor';
import { CELL_EDITOR_STYLES } from './cell-editor.styles';
import { ChildTableA11yService } from '../../services/child-table-a11y.service';

@Component({
  selector: 'ct-textarea-editor',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div #modalBackdrop class="textarea-modal-backdrop"
      role="dialog" aria-modal="true"
      aria-labelledby="textarea-modal-title"
      (mousedown)="onBackdropClick($event)"
      (keydown)="onModalKeydown($event)">
      <div class="textarea-modal" (mousedown)="$event.stopPropagation()">
        <div class="textarea-modal-header">
          <span class="textarea-modal-title" id="textarea-modal-title">{{ column.label }}</span>
          <button type="button" class="textarea-modal-close" (click)="cancelModal()" aria-label="Close">
            <lucide-angular name="x" [size]="14" aria-hidden="true"></lucide-angular>
          </button>
        </div>
        <textarea #modalInput class="textarea-modal-input"
          [(ngModel)]="editingValue"
          (keydown)="onTextareaKeydown($event)"
          rows="12"
          [attr.aria-label]="column.label + ' text content'"
          aria-describedby="textarea-modal-hint"></textarea>
        <div class="textarea-modal-footer">
          <span class="textarea-modal-hint" id="textarea-modal-hint">{{ modKey }}+Enter to save</span>
          <button type="button" class="textarea-modal-btn cancel" (click)="cancelModal()">Cancel</button>
          <button type="button" class="textarea-modal-btn save" (click)="saveModal()">Save</button>
        </div>
      </div>
    </div>
  `,
  styles: [CELL_EDITOR_STYLES, `
    :host { display: none; }
    .textarea-modal-backdrop {
      position: fixed;
      inset: 0;
      z-index: 10000;
      background: rgba(0,0,0,0.45);
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .textarea-modal {
      width: min(640px, 90vw);
      max-height: 80vh;
      background: var(--og-surface-primary, #fff);
      border-radius: var(--og-radius-lg, 12px);
      box-shadow: 0 8px 32px rgba(0,0,0,0.24);
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    .textarea-modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      border-bottom: 1px solid var(--og-border-default, rgba(0,0,0,0.12));
    }
    .textarea-modal-title { font-weight: 600; font-size: 0.9375rem; }
    .textarea-modal-close {
      background: none; border: none; cursor: pointer; padding: 4px;
      color: var(--og-text-secondary, #666); border-radius: var(--og-radius-sm, 4px);
    }
    .textarea-modal-close:hover { background: var(--og-surface-secondary, #f5f5f5); }
    .textarea-modal-input {
      flex: 1;
      margin: 12px 16px;
      padding: 10px 12px;
      font-family: inherit;
      font-size: 0.875rem;
      line-height: 1.5;
      border: 1px solid var(--og-border-default, rgba(0,0,0,0.2));
      border-radius: var(--og-radius-sm, 6px);
      resize: vertical;
      min-height: 180px;
    }
    .textarea-modal-input:focus {
      outline: 2px solid var(--og-primary, #4b3fff);
      outline-offset: -1px;
    }
    .textarea-modal-footer {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 16px;
      border-top: 1px solid var(--og-border-default, rgba(0,0,0,0.12));
    }
    .textarea-modal-hint { flex: 1; font-size: 0.75rem; color: var(--og-text-secondary, #666); }
    .textarea-modal-btn {
      padding: 6px 16px; border-radius: var(--og-radius-sm, 6px); font-size: 0.8125rem; font-weight: 500;
      cursor: pointer; border: 1px solid var(--og-border-default, rgba(0,0,0,0.2));
      background: var(--og-surface-primary, #fff); color: var(--og-text-primary, #1a1a1a);
    }
    .textarea-modal-btn:hover { background: var(--og-surface-secondary, #f5f5f5); }
    .textarea-modal-btn.save {
      background: var(--og-primary, #4b3fff); color: #fff; border-color: var(--og-primary, #4b3fff);
    }
    .textarea-modal-btn.save:hover { opacity: 0.9; }
    .textarea-modal-btn:focus-visible,
    .textarea-modal-close:focus-visible {
      outline: 2px solid var(--og-primary, #4b3fff);
      outline-offset: 2px;
    }
  `],
})
export class TextareaEditorComponent extends BaseCellEditor implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('modalBackdrop') modalBackdropRef!: ElementRef<HTMLElement>;
  @ViewChild('modalInput') modalInput!: ElementRef<HTMLTextAreaElement>;

  private portalEl: HTMLElement | null = null;

  get modKey(): string {
    if (typeof navigator === 'undefined') return 'Ctrl';
    return /Mac|iPhone|iPod|iPad/i.test(navigator.userAgent) ? '⌘' : 'Ctrl';
  }

  constructor(private readonly cdr: ChangeDetectorRef, private readonly a11y: ChildTableA11yService) { super(); }

  ngOnInit(): void { this.editingValue = this.value ?? ''; }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const el = this.modalBackdropRef?.nativeElement;
      if (el) {
        document.body.appendChild(el);
        this.portalEl = el;
        const ta = el.querySelector('textarea') as HTMLElement;
        if (ta) {
          ta.focus();
          (ta as HTMLTextAreaElement).setSelectionRange(
            (ta as HTMLTextAreaElement).value.length,
            (ta as HTMLTextAreaElement).value.length,
          );
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.portalEl?.parentNode === document.body) {
      document.body.removeChild(this.portalEl);
    }
  }

  onTextareaKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.cancelModal();
    }
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      event.stopPropagation();
      this.syncFromDom();
      this.saveModal();
    }
  }

  onModalKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.cancelModal();
    }
    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  saveModal(): void {
    this.syncFromDom();
    this.save.emit(this.editingValue);
  }

  cancelModal(): void {
    this.cancel.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('textarea-modal-backdrop')) {
      this.saveModal();
    }
  }

  private syncFromDom(): void {
    if (this.modalInput?.nativeElement) {
      this.editingValue = this.modalInput.nativeElement.value;
    }
  }

  private trapFocus(event: KeyboardEvent): void {
    if (this.portalEl) {
      this.a11y.trapFocus(event, this.portalEl);
    }
  }
}
